// Porous-absorber acoustics — the physics behind the custom-panel designer.
//
// This reproduces the absorption-coefficient-vs-frequency curve of Chris
// Whealy's "Porous Absorber Calculator" (whealy.com/acoustics) from its two
// governing inputs: the absorber's flow resistivity σ and its thickness.
// The underlying model is Delany–Bazley as refined by Miki (1990) — a
// well-established empirical fit that needs only σ to predict a porous
// material's characteristic impedance Zc and complex wavenumber k. From those
// we run a transmission-line transform through the layer (optionally over an
// air gap) to a rigid wall, then take α from the surface impedance. Charts
// display Paris (field-incidence) α(f) of that same Zs; the solver still
// consumes normal-incidence α. See absorptionAt(..., incidence).
//
// References:
//   Miki, "Acoustical properties of porous materials — Modifications of
//   Delany–Bazley models", J. Acoust. Soc. Jpn. (E) 11, 1 (1990).

// ── Air constants (20 °C) ──────────────────────────────────────────────────
const RHO0 = 1.21; // air density, kg/m³
const C0 = 343; // speed of sound, m/s
const Z0 = RHO0 * C0; // characteristic impedance of air, ≈ 415 rayl

// ── Minimal complex arithmetic (re, im tuples) ─────────────────────────────
type C = readonly [number, number];
const cadd = (a: C, b: C): C => [a[0] + b[0], a[1] + b[1]];
const csub = (a: C, b: C): C => [a[0] - b[0], a[1] - b[1]];
const cmul = (a: C, b: C): C => [a[0] * b[0] - a[1] * b[1], a[0] * b[1] + a[1] * b[0]];
const cdiv = (a: C, b: C): C => {
  const den = b[0] * b[0] + b[1] * b[1];
  return [(a[0] * b[0] + a[1] * b[1]) / den, (a[1] * b[0] - a[0] * b[1]) / den];
};
const cscale = (a: C, s: number): C => [a[0] * s, a[1] * s];
const cabs2 = (a: C): number => a[0] * a[0] + a[1] * a[1];
const J: C = [0, 1];

// cos / sin of a complex argument z = a + bi.
const ccos = (z: C): C => [Math.cos(z[0]) * Math.cosh(z[1]), -Math.sin(z[0]) * Math.sinh(z[1])];
const csin = (z: C): C => [Math.sin(z[0]) * Math.cosh(z[1]), Math.cos(z[0]) * Math.sinh(z[1])];
const ccot = (z: C): C => cdiv(ccos(z), csin(z));

// ── Delany–Bazley–Miki: Zc and k for a porous layer ────────────────────────
// E = f / σ  (Miki's dimensionless variable; f in Hz, σ in rayl/m = Pa·s/m²).
function miki(f: number, sigma: number): { Zc: C; k: C } {
  const omega = 2 * Math.PI * f;
  const E = f / sigma;
  const eZ = Math.pow(E, -0.632);
  const eK = Math.pow(E, -0.618);
  // Characteristic impedance.
  const Zc: C = [Z0 * (1 + 0.07 * eZ), -Z0 * 0.107 * eZ];
  // Complex wavenumber.
  const k: C = [(omega / C0) * (1 + 0.109 * eK), -(omega / C0) * 0.16 * eK];
  return { Zc, k };
}

// Transmission-line transform: a layer (Zc, k, thickness d) terminated by a
// load impedance Z_L, seen from its front face.
//   Zin = Zc · (Z_L·cos(kd) + j·Zc·sin(kd)) / (Zc·cos(kd) + j·Z_L·sin(kd))
function transform(Zc: C, k: C, d: number, ZL: C): C {
  const kd = cscale(k, d);
  const c = ccos(kd);
  const s = csin(kd);
  const num = cadd(cmul(ZL, c), cmul(cmul(J, Zc), s));
  const den = cadd(cmul(Zc, c), cmul(cmul(J, ZL), s));
  return cmul(Zc, cdiv(num, den));
}

export interface AbsorberSpec {
  /** Flow resistivity σ, rayl/m (Pa·s/m²). */
  sigma: number;
  /** Absorber thickness, metres. */
  thickness: number;
  /** Air gap between the absorber's back face and the rigid wall, metres. */
  airGap?: number;
}

/** `'normal'` = solver input (TMM at θ=0). `'random'` = Paris field-incidence
 *  average of the same Zs — what calculated charts display. */
export type Incidence = 'normal' | 'random';

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** Zs(f) for a hard-backed (optional air-gap) porous layer. Shared by
 *  normal-incidence α and the Paris average so both see the same Miki+TMM. */
function surfaceImpedance(f: number, spec: AbsorberSpec): C | null {
  const { sigma, thickness, airGap = 0 } = spec;
  if (f <= 0 || thickness <= 0 || sigma <= 0) return null;
  const { Zc, k } = miki(f, sigma);
  if (airGap > 0) {
    const k0 = (2 * Math.PI * f) / C0;
    const Zgap: C = [0, -Z0 / Math.tan(k0 * airGap)];
    return transform(Zc, k, thickness, Zgap);
  }
  return cmul([0, -1], cmul(Zc, ccot(cscale(k, thickness))));
}

/** Locally-reacting α(θ): R = (z cosθ − 1)/(z cosθ + 1), z = Zs/Z0. */
function alphaFromZs(Zs: C, cosTheta: number): number {
  const z = cdiv(Zs, [Z0, 0]);
  const zCos = cscale(z, cosTheta);
  const R = cdiv(csub(zCos, [1, 0]), cadd(zCos, [1, 0]));
  return clamp01(1 - cabs2(R));
}

// Paris quadrature — match backend/admin_impedance.py so JS charts and the
// Python /porous_absorber tool agree: 40 angles, 0–78°, sinθ cosθ weights,
// trapezoid. 78° is the lab grazing cutoff; 90° is a noisy near-zero sliver.
const PARIS_MAX_RAD = (78 * Math.PI) / 180;
const PARIS_N = 40;
const PARIS_THETA: number[] = Array.from(
  { length: PARIS_N },
  (_, i) => 1e-4 + (i / (PARIS_N - 1)) * (PARIS_MAX_RAD - 1e-4),
);
const PARIS_W: number[] = PARIS_THETA.map((t) => Math.sin(t) * Math.cos(t));
const PARIS_DT = PARIS_THETA[1]! - PARIS_THETA[0]!;

function trapzUniform(y: number[], dt: number): number {
  let s = (y[0]! + y[y.length - 1]!) * 0.5;
  for (let i = 1; i < y.length - 1; i++) s += y[i]!;
  return s * dt;
}

const PARIS_W_DEN = trapzUniform(PARIS_W, PARIS_DT);

function parisAlpha(Zs: C): number {
  const yw = PARIS_THETA.map((theta, i) => alphaFromZs(Zs, Math.cos(theta)) * PARIS_W[i]!);
  return clamp01(trapzUniform(yw, PARIS_DT) / PARIS_W_DEN);
}

/** Absorption coefficient α(f). Default `'normal'` keeps the solver input
 *  and the golden TMM pins unchanged. Charts pass `'random'`. */
export function absorptionAt(f: number, spec: AbsorberSpec, incidence: Incidence = 'normal'): number {
  const Zs = surfaceImpedance(f, spec);
  if (!Zs) return 0;
  return incidence === 'random' ? parisAlpha(Zs) : alphaFromZs(Zs, 1);
}

// Log-spaced frequency axis shared by the model and the chart so the hover
// crosshair maps Hz↔px exactly like the drawn curve.
export const ABS_F_MIN = 40;
export const ABS_F_MAX = 10000;

export interface AbsorptionCurve {
  freqs: number[];
  alpha: number[];
}

/** Sample α across the audible band. Default Paris (field incidence) — that
 *  is what calculated charts show. Pass `'normal'` to pin the TMM goldens. */
export function absorptionCurve(
  spec: AbsorberSpec,
  points = 140,
  incidence: Incidence = 'random',
): AbsorptionCurve {
  const lo = Math.log10(ABS_F_MIN);
  const hi = Math.log10(ABS_F_MAX);
  const freqs: number[] = [];
  const alpha: number[] = [];
  for (let i = 0; i < points; i++) {
    const f = Math.pow(10, lo + (i / (points - 1)) * (hi - lo));
    freqs.push(f);
    alpha.push(absorptionAt(f, spec, incidence));
  }
  return { freqs, alpha };
}

/** NRC: mean α at 250/500/1k/2k Hz, rounded to 0.05. Default Paris, matching
 *  how lab NRC is published. Pass `'normal'` for the TMM golden pins. */
export function nrc(spec: AbsorberSpec, incidence: Incidence = 'random'): number {
  const bands = [250, 500, 1000, 2000];
  const mean = bands.reduce((s, f) => s + absorptionAt(f, spec, incidence), 0) / bands.length;
  return Math.round(mean / 0.05) * 0.05;
}

// ── Material presets — flow resistivity is the "material" ──────────────────
// Representative published σ values (rayl/m) for common broadband absorbers.
export interface MaterialPreset {
  key: string;
  label: string;
  sigma: number;
}
export const MATERIAL_PRESETS: MaterialPreset[] = [
  { key: 'polyester', label: 'Polyester batt', sigma: 3000 },
  { key: 'cotton',    label: 'Recycled cotton', sigma: 5000 },
  { key: 'fiberglass',label: 'Light fiberglass', sigma: 8000 },
  { key: 'foam',      label: 'Acoustic foam', sigma: 12000 },
  { key: 'owens703',  label: 'Rigid fiberglass (703)', sigma: 17000 },
  { key: 'rockwool',  label: 'Mineral wool', sigma: 30000 },
  { key: 'dense',     label: 'Dense mineral wool', sigma: 50000 },
];
